import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Loader2, Mail, RefreshCw, Search, Send, UserX, Inbox } from 'lucide-react';
import { APPLICATION_STATUS_LABELS, STATUS_BADGE_CLASSES, POSITION_OPTIONS } from '../../application/stepConfig';
import { useToast } from '../../ui/ToastProvider';
import driverApplicationService, { errorMessage } from '../../../services/driverApplicationService';
import { formatDisplayDateTime } from '../../../utils/dateInput';

const FILTERS = [
  { value: 'open', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'submitted', label: 'Needs review' },
  { value: 'changes_requested', label: 'Changes requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Not selected' },
  { value: 'all', label: 'All' }
];

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
    {APPLICATION_STATUS_LABELS[status] || status}
  </span>
);

const positionLabel = (value) => POSITION_OPTIONS.find((o) => o.value === value)?.label || '—';

const lastActivity = (item) => item.lastSavedAt || item.submittedAt || item.decidedAt || item.updatedAt;

/**
 * Applications list shown under the Drivers page "Applications" tab.
 */
const ApplicationsTab = ({ onInvite, refreshKey = 0, onStats }) => {
  const toast = useToast();
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, counts] = await Promise.all([
        driverApplicationService.list({ status: filter, search: debounced || undefined, limit: 100 }),
        driverApplicationService.stats()
      ]);
      setItems(list.applications || []);
      setStats(counts);
      onStats?.(counts);
    } catch (err) {
      setError(errorMessage(err, 'Could not load applications.'));
    } finally {
      setLoading(false);
    }
  }, [filter, debounced, onStats]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (item, action) => {
    setPendingId(item.id);
    try {
      if (action === 'resend') {
        const result = await driverApplicationService.resendInvite(item.id);
        toast.success(result.emailSent ? `Invite re-sent to ${item.email}` : 'New link generated. Email is not configured, so share it manually.');
        if (!result.emailSent && result.inviteUrl) {
          try { await navigator.clipboard.writeText(result.inviteUrl); toast.info('Invite link copied to clipboard'); } catch (e) { /* ignore */ }
        }
      } else if (action === 'revoke') {
        if (!window.confirm(`Withdraw the invite for ${item.applicantName}? Their link will stop working.`)) { setPendingId(null); return; }
        await driverApplicationService.revokeInvite(item.id);
        toast.success('Invite withdrawn');
      }
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPendingId(null);
    }
  };

  const counts = useMemo(() => stats || {}, [stats]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Driver Applications</h2>
            <p className="mt-1 text-sm text-gray-500">
              {counts.needsReview ? `${counts.needsReview} waiting for review · ` : ''}{counts.open ?? 0} active · {counts.hired ?? 0} hired
            </p>
          </div>
          <button type="button" onClick={onInvite} className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <Send className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" /> Invite Driver
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              aria-label="Search applications"
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Filter by status">
            {FILTERS.map((f) => {
              const count = f.value === 'all' ? counts.total : f.value === 'open' ? counts.open : counts[f.value];
              const active = filter === f.value;
              return (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(f.value)}
                  className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400'}`}
                >
                  {f.label}{typeof count === 'number' ? ` (${count})` : ''}
                </button>
              );
            })}
          </div>
        </div>

        {error && <p className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" /> Loading applications…
          </div>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white py-14 text-center">
            <Inbox className="mx-auto h-12 w-12 text-gray-400" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-medium text-gray-900">No applications{filter !== 'all' ? ' match this filter' : ' yet'}</h3>
            <p className="mt-1 text-sm text-gray-500">Invite a driver and they will appear here as soon as the email goes out.</p>
            <button type="button" onClick={onInvite} className="btn-primary mt-5">Invite your first driver</button>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="mt-4 space-y-3 md:hidden">
              {items.map((item) => (
                <li key={item.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">{item.applicantName || item.email}</p>
                      <p className="truncate text-xs text-gray-500">{item.email}</p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">{positionLabel(item.positionType)} · {item.progressPercent}% complete · last activity {formatDisplayDateTime(lastActivity(item)) || '—'}</p>
                  <RowActions item={item} pending={pendingId === item.id} onAct={act} compact />
                </li>
              ))}
            </ul>

            {/* Desktop table */}
            <div className="mt-4 hidden overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Applicant', 'Status', 'Progress', 'Position', 'Invited by', 'Last activity', ''].map((h) => (
                      <th key={h} scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Link to={`/drivers/applications/${item.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-700 hover:underline">{item.applicantName || '—'}</Link>
                        <p className="text-xs text-gray-500">{item.email}</p>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200" aria-hidden="true"><div className="h-full bg-blue-500" style={{ width: `${item.progressPercent}%` }} /></div>
                          <span className="text-xs text-gray-600">{item.progressPercent}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{positionLabel(item.positionType)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.invitedBy || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDisplayDateTime(lastActivity(item)) || '—'}</td>
                      <td className="px-4 py-3 text-right"><RowActions item={item} pending={pendingId === item.id} onAct={act} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const RowActions = ({ item, pending, onAct, compact = false }) => {
  const canResend = ['invited', 'expired'].includes(item.status);
  const canRevoke = ['invited', 'in_progress', 'expired'].includes(item.status);
  const needsReview = item.status === 'submitted';
  const base = 'inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';
  return (
    <div className={`flex flex-wrap gap-2 ${compact ? 'mt-3' : 'justify-end'}`}>
      <Link
        to={`/drivers/applications/${item.id}`}
        className={`${base} ${needsReview ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
      >
        <Eye className="h-3.5 w-3.5" aria-hidden="true" /> {needsReview ? 'Review' : 'View'}
      </Link>
      {canResend && (
        <button type="button" disabled={pending} onClick={() => onAct(item, 'resend')} className={`${base} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
          {pending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Mail className="h-3.5 w-3.5" aria-hidden="true" />} {item.status === 'expired' ? 'Send new invite' : 'Resend'}
        </button>
      )}
      {canRevoke && (
        <button type="button" disabled={pending} onClick={() => onAct(item, 'revoke')} className={`${base} border-gray-300 bg-white text-gray-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700`}>
          <UserX className="h-3.5 w-3.5" aria-hidden="true" /> Withdraw
        </button>
      )}
    </div>
  );
};

export default ApplicationsTab;
